import React from 'react';

import TextInput from 'sharedreact/components/TextInput';
import NumberSupport from 'sharedreact/utils/NumberSupport';
import ValidationRules from 'sharedreact/utils/ValidationRules';

type Props = {
  className?: string,
  id: string,
  name: string,
  value: number | string, // string in case an empty string was entered or the string can't be converted into a number
  decimals: number,
  precision?: number,
  greaterThanOrEqualTo?: number,
  greaterThan?: number,
  lessThanOrEqualTo?: number,
  lessThan?: number,
  disabled?: boolean,
  validationTooltipPlacement?: string,
  validationTooltipClassName?: string,
  children?: React.ReactNode,

  onChangeValue?: (name: string, value: string | number, event?: React.FocusEvent<HTMLInputElement, Element>) => void
};

type DefaultProps = Required<Pick<Props, 'onChangeValue'>>;

type PropsWithDefaults = Omit<Props, keyof DefaultProps> & DefaultProps;

class NumberInput extends React.Component<Props> {
  static defaultProps: DefaultProps = {
    onChangeValue: () => {}
   };

  private _isValueChanged: boolean;

  constructor (props: Props) {
    super(props);

    this._isValueChanged = false;
    this.handleChangeValue = this.handleChangeValue.bind(this);
    this.handleLazyValueChange = this.handleLazyValueChange.bind(this);
  }

  handleLazyValueChange (
    name: string,
    valueString: string,
    event?: React.FocusEvent<HTMLInputElement, Element>
  ) {
    if (this._isValueChanged) {
      const { onChangeValue } = (this.props as PropsWithDefaults);

      let storeValue: number | string = valueString;
      if (valueString.length > 0) {
        const numberValue: number | null = NumberSupport.stringToNumber(valueString);

        if (numberValue != null) {
          storeValue = numberValue;
        }
      }

      onChangeValue(name, storeValue, event);

      this._isValueChanged = false;
    }
  }

  handleChangeValue (
    name: string,
    valueString: string,
    event?: React.ChangeEvent<HTMLInputElement> | null
  ) {
    this._isValueChanged = true;
  }

  override render (): React.ReactNode {
    const { className, id, name, value, decimals, precision, greaterThanOrEqualTo,
      greaterThan, lessThanOrEqualTo, lessThan, disabled, children,
      validationTooltipPlacement, validationTooltipClassName
    } = (this.props as PropsWithDefaults);

    const numberValidations = ValidationRules.addNumberRules(
      name,
      greaterThanOrEqualTo,
      greaterThan,
      lessThanOrEqualTo,
      lessThan,
      precision
    );

    const validations = [ 'required' ].concat(numberValidations);

    let displayValue: string = '';
    if (typeof value === 'number') {
      displayValue = NumberSupport.numberToString(value, decimals);
    } else {
      displayValue = value;
    }

    return (
      <TextInput
        className={className}
        id={id}
        name={name}
        value={displayValue}
        disabled={disabled}
        onBlur={this.handleLazyValueChange}
        validations={validations}
        validationTooltipPlacement={validationTooltipPlacement}
        validationTooltipClassName={validationTooltipClassName}
        onChangeValue={this.handleChangeValue}
        onLazyChangeValue={this.handleLazyValueChange}
      >
        {children}
      </TextInput>
    );
  }
}

export default NumberInput;
